import React, { useContext, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Checkbox, TableCell, TableRow } from "@mui/material";
import { IconButton, TextField } from "@totalsoft/rocket-ui";
import PersonIcon from "@mui/icons-material/Person";
import CancelIcon from "@mui/icons-material/Cancel";
import Autosuggest from "components/common/inputs/Autosuggest";
import PartnerShareholderParentRowComponent from "./ShareholderParentRowComponent";
import { onCheckBoxChange } from "utils/propertyChangeAdapters";
import { isValid, getErrors } from "@totalsoft/pure-validations-react";
import { ParamsContext } from "providers/ParamsContext";
import { withParams } from "../../../../providers/ParamsConsumer";
import PhoneNumberField from "components/common/PhoneNumberTextField";

export const ShareholderRowComponent = ({
  shareholder,
  loadPartners,
  onPropertyChange,
  deleteShareholder,
  loadPartnerDetails,
  idx,
  validationShareholderSelection,
  hasWritePermission,
  params,
  openPartnerWindow,
  viewShareholderPhone
}) => {
  const { cvsChPercent } = useContext(ParamsContext);
  const { t } = useTranslation();

  const partnerValue = useMemo(() => {
    if (!shareholder.shareHolderPartnerId) return null;
    return { id: shareholder.shareHolderPartnerId, uniqueID: shareholder.uniqueID };
  }, [shareholder]);

  return (
    <>
      <TableRow>
        <TableCell>
          <div id={"shareholder_" + idx + "_uniqueID"}>
            <Autosuggest
              value={partnerValue}
              onChange={onPropertyChange("shareHolderPartnerId", "uniqueID", loadPartnerDetails)}
              disabled={!hasWritePermission}
              isClearable={false}
              valueKey="id"
              labelKey="uniqueID"
              loadOptions={loadPartners}
              error={!isValid(validationShareholderSelection[idx].uniqueID)}
              helperText={getErrors(validationShareholderSelection[idx].uniqueID)}
            />
          </div>
        </TableCell>
        <TableCell>
          <TextField
            id={"shareholder_" + idx + "_partnerName"}
            disabled
            name={"shareHolderPartnerName"}
            fullWidth
            value={shareholder.shareHolderPartnerName || ""}
            onChange={onPropertyChange("shareHolderPartnerName")}
          />
        </TableCell>
        {viewShareholderPhone && (
          <TableCell>
            <PhoneNumberField
              defaultCountry={params.defaultCountryCode}
              id={"shareholder_" + idx + "_phoneNumber"}
              disabled
              name={"shareHolderPhoneNumber"}
              fullWidth
              value={shareholder.shareHolderPhoneNumber || ""}
              onChange={onPropertyChange("shareHolderPhoneNumber")}
            />
          </TableCell>
        )}
        <TableCell sx={{ textAlign: "center" }}>
          <TextField
            id={"shareholder_" + idx + "_sharePercent"}
            name={"sharePercent"}
            fullWidth
            isNumeric
            value={shareholder.sharePercent || ""}
            inputProps={{ decimalScale: cvsChPercent }}
            onChange={onPropertyChange("sharePercent")}
            disabled={!hasWritePermission}
            error={!isValid(validationShareholderSelection[idx].sharePercent)}
            helperText={getErrors(validationShareholderSelection[idx].sharePercent)}
          />
        </TableCell>
        <TableCell sx={{ textAlign: "center" }}>
          <Checkbox
            id={"shareholder_" + idx + "_isRealBeneficiary"}
            name={"isRealBeneficiary"}
            checked={shareholder.isRealBeneficiary || false}
            onChange={onCheckBoxChange(onPropertyChange("isRealBeneficiary"))}
            disabled={!hasWritePermission}
          />
        </TableCell>
        <TableCell>
          <IconButton
            id={"shareholder_" + idx + "_editShareholder"}
            color="primary"
            variant="text"
            onClick={openPartnerWindow(shareholder.shareHolderPartnerId)}
            tooltip={t("Tooltips.EditShareholder")}
          >
            <PersonIcon />
          </IconButton>
        </TableCell>
        <TableCell>
          <IconButton
            id={"shareholder_" + idx + "_deleteBtn"}
            color="primary"
            variant="text"
            onClick={() => deleteShareholder()}
            disabled={!hasWritePermission}
          >
            <CancelIcon />
          </IconButton>
        </TableCell>
      </TableRow>
      {shareholder.shareholders &&
        shareholder.shareholders.map((parentShareholder, idx) => {
          return (
            <TableRow key={idx}>
              <TableCell colSpan={7}>
                <PartnerShareholderParentRowComponent
                  shareholder={parentShareholder}
                  openPartnerWindow={openPartnerWindow}
                />
              </TableCell>
            </TableRow>
          );
        })}
    </>
  );
};

ShareholderRowComponent.propTypes = {
  onPropertyChange: PropTypes.func.isRequired,
  shareholder: PropTypes.object.isRequired,
  loadPartners: PropTypes.func.isRequired,
  deleteShareholder: PropTypes.func.isRequired,
  loadPartnerDetails: PropTypes.func.isRequired,
  idx: PropTypes.number,
  validationShareholderSelection: PropTypes.object,
  hasWritePermission: PropTypes.bool.isRequired,
  params: PropTypes.object,
  openPartnerWindow: PropTypes.func.isRequired,
  viewShareholderPhone: PropTypes.bool.isRequired
};
export default withParams(ShareholderRowComponent);
